import { useSetAtom } from "jotai";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useNavigation } from "src/utils/navigation";
import { proofingUrl } from "../SpacePlanning/review/reviewNavigation";
import {
  MetricBelowFilter,
  Page,
  PageStatus,
  Tooltip,
  ViewAsStoreTemplate,
  type MetricBelowFilterValue,
} from "src/components";
import {
  Button,
  Checkbox,
  Color,
  Flex,
  HorizontalDivider,
  Icon,
  Pagination,
  Scroller,
  SearchUncontrolled,
  Select,
  SubgridTable,
  Text,
} from "src/elements";
import styled from "styled-components";

import { MerchflowHeader } from "../../components/merchflows/MerchflowHeader/MerchflowHeader";

import { StoresGridLegend, StoresListLegend } from "../../components/merchflows/Legends";
import { highlightsModeAtom } from "src/components/Planogram/store/atoms";
import { ResponseGetMerchflows_merchflowId_Details } from "@CommonApi/merchflows/_merchflowId_/details";
import { useApi } from "src/api";

import { useAsyncState } from "src/hooks/useAsyncState";
import { useConfirmDeliveryConflicts } from "src/hooks/useConfirmDeliveryConflicts";
import { useQueryClient } from "@tanstack/react-query";
import {
  getStorePlanogramReviewItems,
  getStoresAllTableColumns,
  useAestheticsColumnLabels,
} from "./store/columns";
import {
  createPlanogramPreviewRequestCoordinator,
  type PlanogramPreviewTarget,
} from "./store/planogramPreviewRequests";
import {
  getStoresPollingDecision,
  isStorePogInProgress,
  MAX_PDF_STATUS_POLLS,
} from "./store/utils";
import { merchflows_merchflowId_StoresAllHistoryAtom } from "./store/atoms";
import { useStorePlanogramReviewModal } from "src/modals/StorePlanogramReview/store/hooks";
import { useBasePlanogramReviewModal } from "src/modals/BasePlanogramReview/store/hooks";
import { useSearchParams } from "src/utils/useSearchParams";
import { usePaginationPage } from "src/elements/Pagination";
import { useQueryState } from "src/hooks/queryState";
import { ResponseMerchFlowApplyFilters } from "@CommonApi/merchandise";
import { StorePogsExporter } from "src/components/StorePogsExporter/StorePogsExporter";
import { PdfExportBatchStatus } from "src/components/PdfExportBatchStatus/PdfExportBatchStatus";
import { SelectStorePogTrigger } from "./components/SelectStorePogTrigger";
import { useViolationsReportModal } from "src/modals/ViolationsReport/store/hooks";
import { usePowerBiReportModal } from "src/modals/PowerBiReportModal/hooks";
import { useLoaders } from "src/components/InlineLoader";
import { defaultStorePogVersion } from "src/utils/storePogVersions";
import { pogWarning } from "src/utils/pogWarning";
import { useToasts } from "src/components/Toasts";
import { useTranslation } from "src/i18n";
import {
  RoutePutMerchflows_MerchflowId_StoresApprovals,
  type StoreApprovalFilters,
} from "@CommonApi/merchflows/_merchflowId_/stores/approvals";
import {
  ResponsePostMerchflows_MerchflowId_StoresPogsMarkDelivered,
  RoutePostMerchflows_MerchflowId_StoresPogsMarkDelivered,
} from "@CommonApi/merchflows/_merchflowId_/stores/pogs/mark-delivered";
import { PogOverview } from "../Merchflows_merchflowId_Stores_storeCode/components/PogOverview";
import { uuid as newUuid, pages } from "src/utils";

import type {
  AllStoresSortableColumn,
  ParamsGetMerchflows_merchflowId_StoresAll,
} from "@CommonApi/merchflows/_merchflowId_/stores/all";
import { ResponseGetMerchflows_merchflowId_StoresAll } from "@CommonApi/merchflows/_merchflowId_/stores/all";
import type { ResponseGetMerchflowStorePog_storePogId_ } from "@CommonApi/merchflow/store/pog/_storePogId_";
import type { OneStoreViewRow } from "@CommonTypes/merchflow/OneStoreView";
import { useUpdateStoreProductsModal } from "src/modals/UpdateStoreProducts/store/hooks";
import { useStatusModal } from "../../modals/StatusModal/store/hooks";
import { ResponseGetMerchflow_merchflowId_StorePogTriggerAllFinished } from "@CommonApi/merchflow/_merchflowId_/store-pog-trigger/all-finished";
import { routeToUrl } from "@CommonUtils/routes";
import { useUser } from "src/atoms/user";
import { hasPermission, PermissionCode } from "@CommonUtils/RolesPermissions";
import type { FlowUpdateStorePogTriggerResponse } from "@CommonApi/merchflows/_merchflowId_/flow-update/_storePogTriggerId_";
import {
  formatFlowUpdateStorePogTriggerError,
  isFlowUpdateStorePogTriggerError,
  storePogIdsWhereBrandsMayCrossAisleBreak,
} from "src/utils/flowUpdate";
import { getNextStoresAllSort } from "./store/sort";
import {
  AisleBreakBrandLevelNotice,
  type UnresolvedBrandLevelStores,
} from "src/components/AisleBreakBrandLevel/AisleBreakBrandLevelNotice";
import { useStorePlanogramRulesModal } from "src/modals/StorePlanogramRules/store/hooks";
import type { ResponsePostMerchflows_merchflowId_ExportsBatch } from "@CommonApi/merchflows/_merchflowId_/exports/batch";
import type { ResponseGetConfigCopilotVerdict } from "@CommonApi/merchflows/_merchflowId_/config-copilot";
import { NO_STORE_POG_TRIGGER } from "@CommonApi/merchflow/_merchflowId_/store-pog-trigger/all-finished";
import { getConfigCopilotVerdict } from "src/components/ConfigCopilot/store/api";
import { OVerdict } from "src/components/ConfigCopilot/components/OVerdict";
import { useOQa } from "src/components/ConfigCopilot/store/useOQa";

// Caps the inline skipped-stores banner text so a merchflow with a large number of skipped
// stores doesn't produce an unbounded, unreadable joined list.
const MAX_INLINE_SKIPPED_STORES = 20;

const VERDICT_POLL_MS = 5000;

const VERDICT_MAX_FAILURES = 3;

const PogsContainer = styled(Flex)`
  min-height: 0;
  flex-grow: 1;
  flex-direction: column;
  border: 1px solid ${Color.lightGrayHover};
  border-radius: 3px;
`;

export const PageMerchflows_merchflowId_StoresAll = () => {
  const { navigate } = useNavigation();
  const { merchflowId: _merchflowId } = useParams<{
    merchflowId: string;
  }>();
  const { searchParams } = useSearchParams();
  const merchflowId = Number(_merchflowId);
  const { user } = useUser();
  const canUpdateProducts = user
    ? hasPermission(user.roleCode, PermissionCode.INPUTS_UPDATE)
    : false;
  const canSetAisleBreakBrandLevel = user
    ? hasPermission(user.roleCode, PermissionCode.MERCHANT_SETTINGS_UPDATE)
    : false;
  const { openStorePlanogramRules } = useStorePlanogramRulesModal();

  const {
    getMerchflowDetailsApi,
    getMerchflowStoresAllApi,
    getMerchflowReviewApi,
    getStorePropStatusApi,
    getMerchflowStorePogTriggerAllFinishedApi,
    putMerchflowApprovalsApi,
    postMarkMerchflowStorePogsAsDeliveredApi,
    getMerchflowBasePogApi,
    getMerchflowStorePogApi,
  } = useApi();
  const { openStorePlanogramReviewModal } = useStorePlanogramReviewModal();
  const { openBasePlanogramReviewModal } = useBasePlanogramReviewModal();
  const { openViolationsReportModal } = useViolationsReportModal();
  const { openAllStoresReport, openCategoryRangeReviewReport } = usePowerBiReportModal();
  const { hasLoader } = useLoaders();
  const { openUpdateStoreProductsModal } = useUpdateStoreProductsModal();
  const { t } = useTranslation("common");
  const { t: tDelivery } = useTranslation("planogramDelivery");
  const aestheticsLabels = useAestheticsColumnLabels();
  const queryClient = useQueryClient();
  const confirmDeliveryConflicts = useConfirmDeliveryConflicts();

  const refLoadStoresUuid = useRef<string | null>(null);
  const refLoadStoresTimeout = useRef<NodeJS.Timeout | null>(null);
  const refPdfStatusPolls = useRef(0);
  const refIsPostExportRefreshActive = useRef(false);
  const refLoadStorePropStatusUuid = useRef<string | null>(null);
  const refLoadStorePropStatusTimeout = useRef<NodeJS.Timeout | null>(null);
  const refHasMountedViewMode = useRef(false);
  const refVerdictUuid = useRef<string | null>(null);
  const refVerdictTimeout = useRef<NodeJS.Timeout | null>(null);

  const [storePogTriggers, setStorePogTriggers] =
    useAsyncState<ResponseGetMerchflow_merchflowId_StorePogTriggerAllFinished>();
  const [storePropStatus, setStorePropStatus] = useAsyncState<FlowUpdateStorePogTriggerResponse>();
  const [verdict, setVerdict] = useState<ResponseGetConfigCopilotVerdict | null>(null);

  const [isVerdictPending, setIsVerdictPending] = useState(true);
  const [isVerdictLost, setIsVerdictLost] = useState(false);
  const o = useOQa(merchflowId);
  const [data, setData] = useAsyncState<ResponseGetMerchflows_merchflowId_StoresAll>();
  const [details, setDetails] = useAsyncState<ResponseGetMerchflows_merchflowId_Details>();
  const [merchflowFilter, setMerchflowFilter] = useAsyncState<ResponseMerchFlowApplyFilters>();
  const [page, setPage] = usePaginationPage();
  const refCurrentPage = useRef(page);
  refCurrentPage.current = page;
  const [selection, setSelection] = useState<number[]>([]);
  const [isAllSelected, _setIsAllSelected] = useState(false);
  const [activeStoreCodeSearch, setActiveStoreCodeSearch] = useState("");
  const [sort, setSort] = useState<
    Pick<ParamsGetMerchflows_merchflowId_StoresAll, "sortBy" | "sortDirection">
  >({});
  const [metricBelowFilter, setMetricBelowFilter] = useState<MetricBelowFilterValue>({});
  const [viewMode, setViewMode] = useQueryState<"list" | "grid">("view", "list");
  const [markDeliveredResult, setMarkDeliveredResult] =
    useState<ResponsePostMerchflows_MerchflowId_StoresPogsMarkDelivered | null>(null);
  const [isSkippedStoresExpanded, setIsSkippedStoresExpanded] = useState(false);
  const [pdfExportBatch, setPdfExportBatch] =
    useState<ResponsePostMerchflows_merchflowId_ExportsBatch>();

  useEffect(() => {
    setPdfExportBatch(undefined);
  }, [merchflowId]);

  const [isManualDataLoad, setIsManualDataLoad] = useState(false);
  const [loadingPreviewTarget, setLoadingPreviewTarget] = useState<PlanogramPreviewTarget | null>(
    null,
  );
  const [previewRequestCoordinator] = useState(() =>
    createPlanogramPreviewRequestCoordinator(setLoadingPreviewTarget),
  );
  const setStoresAllHistory = useSetAtom(merchflows_merchflowId_StoresAllHistoryAtom);
  const setHighlightsMode = useSetAtom(highlightsModeAtom);

  const { openStatusModal } = useStatusModal();
  const openErrorDetailsModal = (errorDetails: string) => {
    openStatusModal({
      title: "Error details",
      content: errorDetails,
    });
  };

  const { toast } = useToasts();
  const notifyBasePogUnavailable = () => {
    toast({
      title: t("basePog.unavailableTitle"),
      message: t("basePog.unavailableMessage"),
      type: "WARNING",
    });
  };

  const aisleBreakMayCrossStorePogIds = useMemo(
    () => storePogIdsWhereBrandsMayCrossAisleBreak(storePropStatus),
    [storePropStatus],
  );

  const isLoading = data === undefined || isManualDataLoad;
  const storesPerPage = viewMode === "list" ? 100 : 8;
  const isStoreCodeSearchActive = activeStoreCodeSearch.trim().length > 0;
  const isMetricFilterActive =
    metricBelowFilter.metricFilter !== undefined && metricBelowFilter.metricBelow !== undefined;
  const isViolations = details?.is_violated === true;
  const isApprovalsLoading = hasLoader(RoutePutMerchflows_MerchflowId_StoresApprovals);
  const isMarkDeliveredLoading = hasLoader(RoutePostMerchflows_MerchflowId_StoresPogsMarkDelivered);
  const storePogTriggerIdQueryParam = searchParams["store_pog_trigger_id"];
  // Explicit check, not truthiness: 0 addresses the untriggered generation.
  const requestedStorePogTriggerId =
    storePogTriggerIdQueryParam === undefined || storePogTriggerIdQueryParam === ""
      ? undefined
      : Number(storePogTriggerIdQueryParam);
  const latestStorePogTrigger = defaultStorePogVersion(storePogTriggers);
  const storePogTriggerId =
    requestedStorePogTriggerId ?? latestStorePogTrigger?.store_pog_trigger_id;
  const isNonLatestVersionSelected =
    storePogTriggerId !== undefined &&
    storePogTriggerId !== latestStorePogTrigger?.store_pog_trigger_id;
  const storePogTrigger = storePogTriggers?.find(
    (storePogTrigger) => storePogTrigger.store_pog_trigger_id === storePogTriggerId,
  );

  useEffect(() => {
    loadStorePogTriggers();
    setDetails(getMerchflowDetailsApi(merchflowId));
    setHighlightsMode(false);
    setMerchflowFilter(getMerchflowReviewApi(merchflowId));

    return () => {
      if (refLoadStoresTimeout.current) clearTimeout(refLoadStoresTimeout.current);
      if (refLoadStorePropStatusTimeout.current)
        clearTimeout(refLoadStorePropStatusTimeout.current);
      if (refVerdictTimeout.current) clearTimeout(refVerdictTimeout.current);
      refVerdictUuid.current = null;
      refLoadStoresUuid.current = null;
      refIsPostExportRefreshActive.current = false;
      refLoadStorePropStatusUuid.current = null;
      previewRequestCoordinator.dispose();
    };
  }, []);

  useEffect(() => {
    if (storePogTriggers === undefined) {
      return;
    }

    setIsManualDataLoad(true);
    loadStores();
  }, [
    viewMode,
    page,
    storePogTriggerId,
    activeStoreCodeSearch,
    sort.sortBy,
    sort.sortDirection,
    metricBelowFilter.metricFilter,
    metricBelowFilter.metricBelow,
    storePogTriggers,
  ]);

  useEffect(() => {
    // Store propagation status API only supports FLOW_UPDATE triggers; skip and clear when selecting STORE_PROP or no trigger
    if (!storePogTriggerId) {
      setStorePropStatus(null);
    } else if (storePogTrigger?.trigger_type === "FLOW_UPDATE") {
      loadStorePropStatus();
    } else {
      setStorePropStatus(null);
    }
  }, [storePogTriggerId, storePogTrigger?.trigger_type]);

  // O may read the newest running trigger while the table still shows the last finished trigger.

  const verdictRun = requestedStorePogTriggerId;

  const showsVerdict =
    isVerdictPending || isVerdictLost || (verdict?.status !== "none" && Boolean(verdict?.line));

  useEffect(() => {
    if (refVerdictTimeout.current) clearTimeout(refVerdictTimeout.current);
    refVerdictUuid.current = null;
    setVerdict(null);
    setIsVerdictLost(false);
    const isAsked = verdictRun !== NO_STORE_POG_TRIGGER;
    setIsVerdictPending(isAsked);
    if (isAsked) void loadVerdict();
  }, [verdictRun]);

  useEffect(() => {
    if (!refHasMountedViewMode.current) {
      refHasMountedViewMode.current = true;
      return;
    }

    setData(undefined);
    setPage(1);
  }, [viewMode]);

  const loadStorePogTriggers = async () => {
    setStorePogTriggers(await getMerchflowStorePogTriggerAllFinishedApi(merchflowId));
  };

  const loadStorePropStatus = async (uuid?: string) => {
    if (!storePogTriggerId) return;

    if (!uuid) {
      refLoadStorePropStatusUuid.current = newUuid();
      uuid = refLoadStorePropStatusUuid.current;
      if (refLoadStorePropStatusTimeout.current) {
        clearTimeout(refLoadStorePropStatusTimeout.current);
      }
    }

    const response = await getStorePropStatusApi(merchflowId, storePogTriggerId);
    if (refLoadStorePropStatusUuid.current !== uuid) return;
    setStorePropStatus(response);

    if (!isFlowUpdateStorePogTriggerError(response) && response && response.in_progress_count > 0) {
      refLoadStorePropStatusTimeout.current = setTimeout(() => {
        loadStorePropStatus(uuid);
      }, 5000);
    }
  };

  const loadVerdict = async (uuid?: string, wasRunning = false, failures = 0) => {
    if (!uuid) {
      refVerdictUuid.current = newUuid();
      uuid = refVerdictUuid.current;
    }

    let response: ResponseGetConfigCopilotVerdict;
    try {
      response = await getConfigCopilotVerdict(
        merchflowId,
        verdictRun === undefined ? {} : { store_pog_trigger_id: verdictRun },
      );
    } catch {
      if (refVerdictUuid.current !== uuid) return;
      if (failures + 1 < VERDICT_MAX_FAILURES) {
        refVerdictTimeout.current = setTimeout(() => {
          void loadVerdict(uuid, wasRunning, failures + 1);
        }, VERDICT_POLL_MS);
        return;
      }
      setIsVerdictPending(false);
      setIsVerdictLost(true);
      return;
    }
    if (refVerdictUuid.current !== uuid) return;
    setVerdict(response);
    setIsVerdictPending(false);
    setIsVerdictLost(false);

    if (response.status === "running") {
      refVerdictTimeout.current = setTimeout(() => {
        void loadVerdict(uuid, true);
      }, VERDICT_POLL_MS);
    } else if (wasRunning && verdictRun === undefined) {
      void loadStorePogTriggers();
    }
  };

  const loadStores = async (uuid?: string, startPostExportRefresh = false) => {
    if (!uuid) {
      refLoadStoresUuid.current = newUuid();
      uuid = refLoadStoresUuid.current;
      refPdfStatusPolls.current = 0;
      refIsPostExportRefreshActive.current = startPostExportRefresh;
      if (refLoadStoresTimeout.current) {
        clearTimeout(refLoadStoresTimeout.current);
      }
    }

    const queryParams: ParamsGetMerchflows_merchflowId_StoresAll = {
      page: refCurrentPage.current,
      size: storesPerPage,
      store_pog_trigger_id: storePogTriggerId,
      viewMode,
    };

    if (activeStoreCodeSearch) {
      queryParams.storeCode = activeStoreCodeSearch;
    }

    if (sort.sortBy && sort.sortDirection) {
      queryParams.sortBy = sort.sortBy;
      queryParams.sortDirection = sort.sortDirection;
    }

    // Both halves or neither: the server ignores a lone one, and sending it
    // would only make the request look like a filter that is not applied.
    if (metricBelowFilter.metricFilter && metricBelowFilter.metricBelow !== undefined) {
      queryParams.metric_filter = metricBelowFilter.metricFilter;
      queryParams.metric_below = metricBelowFilter.metricBelow;
    }

    const response = await getMerchflowStoresAllApi(merchflowId, queryParams);
    if (refLoadStoresUuid.current !== uuid) {
      return;
    }

    setData(response);
    setIsManualDataLoad(false);

    const includePdfPolling = refPdfStatusPolls.current < MAX_PDF_STATUS_POLLS;
    const pollingDecision =
      response &&
      getStoresPollingDecision(
        response.data,
        includePdfPolling,
        refIsPostExportRefreshActive.current,
      );

    if (pollingDecision?.shouldPoll) {
      if (pollingDecision.countsTowardPdfBudget) {
        refPdfStatusPolls.current += 1;
      }
      refLoadStoresTimeout.current = setTimeout(() => {
        loadStores(uuid);
      }, 20_000);
    }
  };

  const clearSelection = useCallback(() => {
    _setIsAllSelected(false);
    setSelection([]);
  }, []);

  useEffect(() => {
    clearSelection();
  }, [storePogTriggerId, clearSelection]);

  const setIsAllSelected = (isAllSelected: boolean) => {
    if (isStoreCodeSearchActive && isAllSelected) {
      return;
    }

    _setIsAllSelected(isAllSelected);
    setSelection([]);
  };

  // Select-all approves exactly the stores the list shows, so it carries the list's filters.
  const approvalFilters: StoreApprovalFilters = {
    store_pog_trigger_id: storePogTriggerId,
    storeCode: activeStoreCodeSearch || undefined,
    ...(isMetricFilterActive && {
      metric_filter: metricBelowFilter.metricFilter,
      metric_below: metricBelowFilter.metricBelow,
    }),
  };

  const setStoreCodeSearchAndResetPage = useCallback(
    (nextSearch: string) => {
      if (nextSearch === activeStoreCodeSearch) {
        return;
      }
      setActiveStoreCodeSearch(nextSearch);
      clearSelection();
      setPage(1);
    },
    [activeStoreCodeSearch, clearSelection, setPage],
  );

  const setSortAndResetPage = (sortBy: AllStoresSortableColumn) => {
    setSort((currentSort) => getNextStoresAllSort(currentSort, sortBy));
    setPage(1);
  };

  const setMetricBelowFilterAndResetPage = useCallback(
    (nextFilter: MetricBelowFilterValue) => {
      setMetricBelowFilter(nextFilter);
      clearSelection();
      setPage(1);
    },
    [clearSelection, setPage],
  );

  const approveSelectedStorePogs = async () => {
    const response = await putMerchflowApprovalsApi(
      merchflowId,
      isAllSelected
        ? {
            approveAllExclude: selection.map((storePogId) => ({
              store_pog_id: storePogId,
            })),
            filters: approvalFilters,
          }
        : {
            approved: selection.map((storePogId) => ({
              store_pog_id: storePogId,
            })),
          },
    );

    if (response) {
      setData(undefined);
      clearSelection();
      loadStores();
    }
  };

  const unapproveSelectedStorePogs = async () => {
    const response = await putMerchflowApprovalsApi(
      merchflowId,
      isAllSelected
        ? {
            unapproveAllExclude: selection.map((storePogId) => ({
              store_pog_id: storePogId,
            })),
            filters: approvalFilters,
          }
        : {
            unapproved: selection.map((storePogId) => ({
              store_pog_id: storePogId,
            })),
          },
    );

    if (response) {
      setData(undefined);
      clearSelection();
      loadStores();
    }
  };

  const markWholeMerchflowAsDelivered = () =>
    confirmDeliveryConflicts({ merchflowId }, async () => {
      const response = await postMarkMerchflowStorePogsAsDeliveredApi(merchflowId);

      if (response) {
        setMarkDeliveredResult(response);
        setIsSkippedStoresExpanded(false);
        void queryClient.invalidateQueries({ queryKey: ["space-planning"] });
      }
    });

  const openStoreIntegrityReport = () => {
    if (!merchflowFilter?.id || !details?.latest_base_pog_id) {
      return;
    }

    openAllStoresReport({
      filterConfigId: merchflowFilter.id,
      basePogId: details.latest_base_pog_id,
    });
  };

  const openAllStoresCategoryRangeReviewReport = () => {
    if (data?.project_id) {
      openCategoryRangeReviewReport(data.project_id);
    }
  };

  const generateStoresAfterBrandLevel = async ({
    basePogId,
    storeCodes,
  }: UnresolvedBrandLevelStores) => {
    const basePog = await getMerchflowBasePogApi(basePogId);
    if (!basePog) return;
    openStorePlanogramRules({
      mode: "store",
      merchflowId,
      basePogId,
      isBasePogFavorited: basePog.favorited === true,
      cdtReleaseId: basePog.cdtReleaseId,
      storeCodes,
      defaultHighDensityEnabled: basePog.flow_review?.config?.flowType === "UHD" ? true : undefined,
    });
  };

  const openGridStorePlanogram = async (store: OneStoreViewRow) => {
    if (!data || !store.store_pog) return;

    let storePogData = store.store_pog.store_pog_data;
    let storePogResponse: ResponseGetMerchflowStorePog_storePogId_ | null = null;

    if (storePogData) {
      previewRequestCoordinator.cancelPendingRequest();
    } else {
      storePogResponse = await previewRequestCoordinator.runLatestRequest(
        { type: "store", id: store.store_pog.store_pog_id },
        // Without ranging, as the list view asks: the popup never shows those counts.
        () => getMerchflowStorePogApi(store.store_pog.store_pog_id, false),
      );
      storePogData = storePogResponse?.pog ?? undefined;
    }

    if (!storePogData) return;

    openStorePlanogramReviewModal({
      merchflowId,
      storePogId: store.store_pog.store_pog_id,
      storePlanograms: getStorePlanogramReviewItems(
        data.data,
        store.store_pog.store_pog_id,
        storePogData,
        storePogResponse,
      ),
    });
  };

  return (
    <Page>
      <MerchflowHeader merchflowId={merchflowId} qaPage={{ kind: "stores", storePogTriggerId }} />

      <Flex column borderColor={Color.lightGrayHover} borderRadius="3px">
        <Flex gap="10px" justify="between" align="center" padding="10px">
          <Flex gap="16px" align="center" flexGrow minWidth="0">
            <Flex
              width="280px"
              flexShrink={0}
              align="center"
              role="group"
              aria-label="Export status"
              data-testid="export-status-area"
            >
              <PdfExportBatchStatus merchflowId={merchflowId} batch={pdfExportBatch} />
            </Flex>

            {showsVerdict ? null : isFlowUpdateStorePogTriggerError(storePropStatus) ? (
              <Flex gap="5px" align="center">
                <Icon name="alert" size={14} color={Color.red} />

                <Flex column gap="2px">
                  <Text color={Color.red} wordBreak="break-word">
                    {storePropStatus.message}
                  </Text>

                  {storePropStatus.errors.map((error, index) => (
                    <Text
                      key={`${error}-${index}`}
                      variant="small2"
                      color={Color.red}
                      wordBreak="break-word"
                    >
                      {error}
                    </Text>
                  ))}
                </Flex>

                <Button
                  variant="borderOnly"
                  color="primary"
                  size="small"
                  onClick={() =>
                    openErrorDetailsModal(formatFlowUpdateStorePogTriggerError(storePropStatus))
                  }
                >
                  Details
                </Button>
              </Flex>
            ) : (
              storePropStatus?.type === "FLOW_UPDATE" && (
                <Flex gap="5px" align="center">
                  <Icon
                    name={
                      (storePropStatus.in_progress_count === 0 && "checkmarkCircle") || "spinner"
                    }
                    size={14}
                  />

                  <Text color={Color.primaryActive}>
                    {storePropStatus.in_progress_count === 0
                      ? "Store propagation completed"
                      : "Store propagation is running..."}
                  </Text>

                  <Tooltip textAlign="left">
                    <Flex column>
                      {storePropStatus.in_progress_count > 0 && (
                        <div>
                          In progress: <b>{storePropStatus.in_progress_count}</b>
                        </div>
                      )}

                      <div>
                        Fully updated: <b>{storePropStatus.success_result_count}</b>
                      </div>

                      <div>
                        Partially updated: <b>{storePropStatus.partial_result_count}</b>
                      </div>

                      <div>
                        Failed: <b>{storePropStatus.fail_result_count}</b>
                      </div>
                    </Flex>
                  </Tooltip>
                </Flex>
              )
            )}
          </Flex>

          <Flex gap="10px" flexShrink={0} data-testid="store-toolbar-actions">
            <Button
              color="primary"
              isDisabled={!data}
              onClick={() => {
                const returnParams = new URLSearchParams(window.location.search);
                if (storePogTriggerId !== undefined)
                  returnParams.set("store_pog_trigger_id", String(storePogTriggerId));
                navigate(
                  proofingUrl(
                    merchflowId,
                    storePogTriggerId,
                    selection,
                    `${window.location.pathname}?${returnParams}`,
                    isAllSelected,
                  ),
                );
              }}
            >
              {isAllSelected
                ? t("reviewSelected")
                : selection.length
                  ? t("reviewSelectedCount", { count: selection.length })
                  : t("reviewPlanograms")}
            </Button>
            {canUpdateProducts && (
              <Button
                color="yellow"
                variant="default"
                onClick={() => data && openUpdateStoreProductsModal(data, merchflowId)}
                isDisabled={!data}
              >
                Update Products
              </Button>
            )}

            <Button
              variant={isViolations ? "default" : "inverted"}
              color="yellow"
              onClick={() => openViolationsReportModal(merchflowId)}
              isDisabled={data === undefined || isViolations !== true}
            >
              Violations
            </Button>

            <Button
              color="greenSmoke"
              isDisabled={data === undefined}
              dropdown={[
                {
                  label: "Update Flow Report",
                  url: routeToUrl(pages.merchflows_merchflowId_UpdateReport_storePogTriggerId, {
                    merchflowId,
                    storePogTriggerId: String(storePogTrigger?.store_pog_trigger_id),
                  }),
                  isDisabled: storePogTrigger?.trigger_type !== "FLOW_UPDATE",
                },
                {
                  label: "Store Integrity Report",
                  onClick: openStoreIntegrityReport,
                },
                {
                  label: "Category Range Review Report",
                  isDisabled: !data?.project_id,
                  onClick: openAllStoresCategoryRangeReviewReport,
                },
              ]}
            >
              Reports
            </Button>

            <Button
              color="greenSmoke"
              isLoading={isApprovalsLoading}
              isDisabled={data === undefined}
              dropdown={[
                {
                  label: "Approve selected",
                  onClick: approveSelectedStorePogs,
                  isDisabled: isAllSelected === false && selection.length === 0,
                },
                {
                  label: "Unapprove selected",
                  onClick: unapproveSelectedStorePogs,
                  isDisabled: isAllSelected === false && selection.length === 0,
                },
              ]}
            >
              Approvals
            </Button>

            <Button
              color="greenSmoke"
              isLoading={isMarkDeliveredLoading}
              isDisabled={data === undefined}
              onClick={markWholeMerchflowAsDelivered}
            >
              {tDelivery("markDelivered.bulkAction")}
            </Button>

            <StorePogsExporter
              merchflowId={merchflowId}
              storePogsIds={selection}
              isDisabled={data === undefined}
              isAllSelected={isAllSelected}
              isNonLatestVersionSelected={isNonLatestVersionSelected}
              // Exports cannot carry the score filter, so across all stores they would include hidden ones.
              areAllStoreActionsDisabled={isStoreCodeSearchActive || isMetricFilterActive}
              disabledTooltip={
                isStoreCodeSearchActive
                  ? "Clear the store code search to export across all approved stores."
                  : t("metricFilter.exportAllUnavailable")
              }
              onPdfExportStatusChange={() => loadStores(undefined, true)}
              onPdfExportBatchChange={setPdfExportBatch}
            />
          </Flex>
        </Flex>

        {/* O's line has a row of its own, held from the first paint, so it never wraps beside the buttons. */}
        {showsVerdict && (
          <Flex
            minHeight="24px"
            align="center"
            padding="0 10px 10px 10px"
            data-testid="o-verdict-row"
          >
            <OVerdict
              merchflowId={merchflowId}
              verdict={verdict}
              shownRun={storePogTriggerId}
              isLost={isVerdictLost}
              {...(verdict ? { isAskOpen: o.isOpen, onAsk: () => o.setOpen(!o.isOpen) } : {})}
            />
          </Flex>
        )}

        {markDeliveredResult && (
          <Flex gap="8px" align="center" flexWrap="wrap" padding="0 10px 10px 10px">
            <Icon
              name={markDeliveredResult.skipped_count > 0 ? "alert" : "checkmarkCircle"}
              size={14}
              color={markDeliveredResult.skipped_count > 0 ? Color.yellow : Color.primaryActive}
            />

            <Text
              color={markDeliveredResult.skipped_count > 0 ? Color.yellow : Color.primaryActive}
            >
              {tDelivery("markDelivered.bulkSummary", {
                delivered: markDeliveredResult.delivered_count,
                alreadyDelivered: markDeliveredResult.already_delivered_count,
              })}
              {markDeliveredResult.skipped_count > 0 &&
                tDelivery("markDelivered.bulkSkippedSuffix", {
                  skipped: markDeliveredResult.skipped_count,
                })}
            </Text>

            {markDeliveredResult.skipped_count > 0 && (
              <Button
                variant="borderOnly"
                color="primary"
                size="small"
                onClick={() => setIsSkippedStoresExpanded((expanded) => !expanded)}
              >
                {isSkippedStoresExpanded
                  ? tDelivery("markDelivered.hideSkipped")
                  : tDelivery("markDelivered.showSkipped")}
              </Button>
            )}

            {isSkippedStoresExpanded && (
              <Text variant="small2" color={Color.textSecondary} wordBreak="break-word">
                {tDelivery("markDelivered.skippedStores", {
                  stores:
                    markDeliveredResult.skipped_stores
                      .slice(0, MAX_INLINE_SKIPPED_STORES)
                      .join(", ") +
                    (markDeliveredResult.skipped_stores.length > MAX_INLINE_SKIPPED_STORES
                      ? ` (+${markDeliveredResult.skipped_stores.length - MAX_INLINE_SKIPPED_STORES} more)`
                      : ""),
                })}
              </Text>
            )}
          </Flex>
        )}

        {data && (
          <AisleBreakBrandLevelNotice
            merchflowId={merchflowId}
            rows={data.data}
            canSetBrandLevel={canSetAisleBreakBrandLevel}
            onCdtChanged={() => loadStores()}
            onGenerate={(stores) => void generateStoresAfterBrandLevel(stores)}
          />
        )}

        <HorizontalDivider color={Color.lightGrayHover} />

        <Flex width="100%" justify="between" padding="10px" margin="5px 0 0 0">
          <Flex gap="10px">
            <ViewAsStoreTemplate merchflowId={merchflowId} />

            {viewMode === "grid" && (
              <Checkbox
                isChecked={isAllSelected}
                setIsChecked={setIsAllSelected}
                label="All Selected"
                isCheckboxLeft
                name="all-selected-store-pogs-grid"
                isDisabled={isStoreCodeSearchActive}
              />
            )}
          </Flex>

          <Flex width="100%" justify="right" gap="10px">
            {isStoreCodeSearchActive && (
              <Flex align="center">
                <Text variant="small2" color={Color.textSecondary}>
                  All-store bulk actions are unavailable while store code search is active.
                </Text>
              </Flex>
            )}

            <MetricBelowFilter
              value={metricBelowFilter}
              onChange={setMetricBelowFilterAndResetPage}
            />

            <SearchUncontrolled
              width="200px"
              placeholder="Search store code"
              onSearch={setStoreCodeSearchAndResetPage}
              isDisabled={data === undefined}
            />

            <SelectStorePogTrigger
              merchflowId={merchflowId}
              storePogTriggers={storePogTriggers}
              storePogTriggerId={storePogTriggerId}
            />

            <Select
              width="100px"
              title="Show as"
              isDisabled={data === undefined}
              value={viewMode}
              setValue={setViewMode}
              options={[
                {
                  label: "Grid",
                  value: "grid",
                },
                {
                  label: "List",
                  value: "list",
                },
              ]}
            />
          </Flex>
        </Flex>
      </Flex>

      <Flex flexGrow column gap="10px">
        <PogsContainer>
          {isLoading && <PageStatus label="Loading stores..." icon="spinner" />}

          {!isLoading && data && viewMode === "list" && (
            <Flex flexGrow column gap="5px" data-testid="store-pogs-list">
              <SubgridTable
                page={page}
                isSortable={false}
                headersNoWrap
                columns={getStoresAllTableColumns({
                  merchflowId,
                  setStoresAllHistory,
                  selection: {
                    selection,
                    setSelection,
                    isAllSelected,
                    setIsAllSelected,
                    isBulkAllDisabled: isStoreCodeSearchActive,
                  },
                  openBasePlanogramReviewModal,
                  openStorePlanogramReviewModal,
                  data: data.data,
                  openErrorDetailsModal,
                  onBasePogUnavailable: notifyBasePogUnavailable,
                  sort: {
                    sortBy: sort.sortBy,
                    sortDirection: sort.sortDirection,
                    onSortChange: setSortAndResetPage,
                  },
                  getMerchflowBasePogApi,
                  getMerchflowStorePogApi,
                  previewRequestCoordinator,
                  loadingPreviewTarget,
                  aisleBreaks: {
                    header: t("aisleBreaks.result.columnHeader"),
                    mayCrossStorePogIds: aisleBreakMayCrossStorePogIds,
                  },
                  aestheticsLabels,
                  cdtVersion:
                    details?.category_id != null
                      ? {
                          categoryId: details.category_id,
                          header: t("storePogsCdtVersion.header"),
                        }
                      : undefined,
                })}
                data={data.data}
              />
            </Flex>
          )}

          {!isLoading && data && viewMode === "grid" && (
            <Flex flexGrow column gap="5px">
              <Scroller>
                <Flex padding="20px" flexWrap="wrap" gap="25px">
                  {data.data.map((store, i) => {
                    const inProgress = store.store_pog
                      ? isStorePogInProgress(store.store_pog.status)
                      : false;
                    const isPreviewLoading =
                      store.store_pog !== undefined &&
                      loadingPreviewTarget?.type === "store" &&
                      loadingPreviewTarget.id === store.store_pog.store_pog_id;

                    return (
                      <Flex key={i}>
                        <PogOverview
                          onClick={() => {
                            if (!isPreviewLoading) {
                              openGridStorePlanogram(store);
                            }
                          }}
                          status={
                            (store.store_pog?.status === "ERROR" && "ERROR") ||
                            (inProgress && "IN_PROGRESS") ||
                            (store.approved_store_pog_amount === 1 && "SUCCESS") ||
                            (store.approved_store_pog_amount > 1 && "WARNING") ||
                            "BORDER"
                          }
                          label={`Store: ${store.store}`}
                          badge={store.store_pog_amount > 1 && String(store.store_pog_amount)}
                          pog={store.store_pog?.store_pog_data}
                          selectionId={store.store_pog?.store_pog_id}
                          isAllSelected={isAllSelected}
                          selection={selection}
                          setSelection={setSelection}
                          warning={pogWarning(store.store_pog?.store_pog_data)}
                          thumbnail={store.store_pog?.thumbnail}
                          statusMessage={
                            (isPreviewLoading && "Loading planogram...") ||
                            (store.store_pog?.status === "ERROR" && store.store_pog?.error) ||
                            (inProgress && "Loading planogram...")
                          }
                        />
                      </Flex>
                    );
                  })}
                </Flex>
              </Scroller>
            </Flex>
          )}
        </PogsContainer>

        <Flex justify="between">
          {viewMode === "list" && <StoresListLegend />}

          {viewMode === "grid" && <StoresGridLegend />}

          <Pagination
            page={page}
            totalPages={data?.totalPages}
            setPage={setPage}
            isLoading={data === undefined}
          />
        </Flex>
      </Flex>
    </Page>
  );
};
